import React from 'react';
import { Box, Button, Checkbox, CircularProgress, Typography } from '@mui/material';
import PanelBlock, { PanelEyebrow, Prose } from './PanelBlock';
import { useLabBasket } from '../../contexts/LabBasketContext';
import { LABOKLIN_RED } from '../../theme/laboklinTheme';
import type { StructuredTest } from '../../types/laboklin';

/**
 * The tests the assistant offered, as an order the veterinarian can assemble.
 *
 * Every line here came back from suggest_lab_tests during the workup, so the name,
 * the service number and the price are the laboratory's own and not a recollection
 * of them. Each carries the question it settles, because a suggestion with no
 * question behind it reads as an advertisement.
 *
 * Nothing on this panel places an order. It fills the same in-memory basket the chat
 * drawer fills, and hands over to MyLab's submission form to be confirmed.
 */

interface NextTestsPanelProps {
  tests: StructuredTest[];
  onConfirm: () => void;
  /**
   * The workup is still running, so "nothing to offer" is not yet an answer.
   *
   * The tests are read off the terminal turn, which is written last - after the engine
   * has ranked and the review has run. So for the minute or two before that lands there
   * are legitimately no tests, and stating "nothing in the catalogue would settle this"
   * during it is a confident wrong answer that then quietly replaces itself.
   */
  waiting?: boolean;
}

export const NextTestsPanel: React.FC<NextTestsPanelProps> = ({ tests, onConfirm, waiting }) => {
  const basket = useLabBasket();

  /**
   * An explicit empty state, rather than no panel at all.
   *
   * `return null` here was half of a reported inconsistency. The other half was
   * upstream - the assistant did not always call suggest_lab_tests - but the effect
   * on screen was the same either way: on some runs of a report the section was
   * there and on others it was simply gone, which reads as the assistant changing
   * its mind rather than as there being nothing to sell. A veterinarian testing the
   * same report repeatedly reported exactly that.
   *
   * Saying "nothing to add" is an answer. Showing nothing is not.
   */
  if (tests.length === 0) {
    return (
      <PanelBlock>
        <PanelEyebrow
          label="Suggested additional tests"
          state={waiting ? 'Working' : 'None'}
        />
        {waiting ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 1 }}>
            <CircularProgress size={14} thickness={5} />
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
              Waiting for the workup to finish before suggesting tests&hellip;
            </Typography>
          </Box>
        ) : (
          <Typography sx={{ fontSize: 13, color: 'text.secondary', lineHeight: 1.55 }}>
            Nothing in Laboklin&rsquo;s catalogue would settle the questions still open on this
            report.
          </Typography>
        )}
      </PanelBlock>
    );
  }

  const selected = tests.filter((t) => basket?.has(t.serviceNo));
  // A total is only honest when every selected line carries a price; one unpriced
  // service would make the sum quietly wrong.
  const allPriced = selected.every((t) => typeof t.price === 'number');
  const total = allPriced ? selected.reduce((sum, t) => sum + (t.price ?? 0), 0) : null;

  return (
    <PanelBlock>
      <PanelEyebrow label="Suggested additional tests" state="Live Laboklin catalogue" />

      {tests.map((test) => {
        const inBasket = basket?.has(test.serviceNo) ?? false;

        return (
          <Box
            key={test.serviceNo}
            sx={{
              display: 'grid',
              gridTemplateColumns: '32px 1fr auto',
              gap: 1,
              alignItems: 'start',
              py: 1.25,
              borderTop: '1px solid #eee',
              '&:first-of-type': { borderTop: 0 },
            }}
          >
            <Checkbox
              size="small"
              checked={inBasket}
              disabled={!basket}
              onChange={() => {
                if (!basket) return;
                if (inBasket) basket.remove(test.serviceNo);
                else
                  basket.add({
                    serviceNo: test.serviceNo,
                    name: test.name,
                    price: test.price,
                    ...(test.settles ? { reason: test.settles } : {}),
                  });
              }}
              sx={{ p: 0, mt: 0.25, color: LABOKLIN_RED, '&.Mui-checked': { color: LABOKLIN_RED } }}
              inputProps={{ 'aria-label': `Add ${test.name} to order` }}
            />
            <Box>
              <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{test.name}</Typography>
              {test.settles && (
                <Typography sx={{ fontSize: 13, color: 'text.secondary' }} component="div"><Prose>{test.settles}</Prose></Typography>
              )}
            </Box>
            <Typography sx={{ fontSize: 13, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
              {test.price !== null ? `€ ${test.price.toFixed(2)}` : ''}
            </Typography>
          </Box>
        );
      })}

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: 1.5,
          pt: 1.5,
          borderTop: '2px solid rgba(0,0,0,0.125)',
          gap: 1,
        }}
      >
        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
          {selected.length} selected
          {total !== null && (
            <Box component="span" sx={{ color: 'text.primary', fontSize: 15, fontWeight: 600, ml: 0.75 }}>
              € {total.toFixed(2)}
            </Box>
          )}
        </Typography>
        <Button
          size="small"
          variant="contained"
          disabled={selected.length === 0}
          onClick={onConfirm}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            bgcolor: LABOKLIN_RED,
            '&:hover': { bgcolor: LABOKLIN_RED, filter: 'brightness(0.9)' },
          }}
        >
          Continue to order
        </Button>
      </Box>

      <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 1 }}>
        Nothing is ordered here. The submission form is where an order is confirmed.
      </Typography>
    </PanelBlock>
  );
};

export default NextTestsPanel;
